import { PDFArray, PDFDict, PDFDocument, PDFHexString, PDFName, PDFNumber, PDFRef, PDFString, type PDFPage } from "pdf-lib";

// pdf-lib's copyPages carries a page's content and its link annotations
// but not the document-level outline, so a merged file silently lost every
// bookmark -- confirmed by merging two bookmarked PDFs and inspecting the
// result (links: 2 of 2 kept, bookmarks: gone). These helpers read a
// source's outline and rebuild it in the merged document with each
// destination remapped onto the copied pages.

export interface OutlineNode {
  title: string;
  /** Zero-based page index in the document it was read from, or null when
   * the destination can't be resolved (e.g. a named destination). */
  pageIndex: number | null;
  children: OutlineNode[];
}

export function readOutline(doc: PDFDocument): OutlineNode[] {
  const ctx = doc.context;
  const root = doc.catalog.lookupMaybe(PDFName.of("Outlines"), PDFDict);
  if (!root) return [];
  const pageRefs = doc.getPages().map(p => p.ref);
  const indexOfPage = (ref: PDFRef) => {
    const i = pageRefs.findIndex(r => r.objectNumber === ref.objectNumber && r.generationNumber === ref.generationNumber);
    return i >= 0 ? i : null;
  };

  const destinationIndex = (item: PDFDict): number | null => {
    let dest = item.get(PDFName.of("Dest"));
    if (!dest) {
      const action = item.lookupMaybe(PDFName.of("A"), PDFDict);
      if (action?.get(PDFName.of("S"))?.toString() === "/GoTo") dest = action.get(PDFName.of("D"));
    }
    if (!dest) return null;
    // Named destinations (a string or a name) aren't arrays: left unresolved. Looking one up as an array
    // used to throw and stop the whole tool (QA RM-1), so any other kind of object is skipped.
    let explicit: PDFArray | undefined;
    try { explicit = ctx.lookupMaybe(dest, PDFArray); } catch { return null; }
    const first = explicit?.get(0);
    return first instanceof PDFRef ? indexOfPage(first) : null;
  };

  const walk = (firstRef: PDFRef | undefined): OutlineNode[] => {
    const nodes: OutlineNode[] = [];
    const seen = new Set<string>(); // guards against a malformed, looping Next chain
    let ref = firstRef;
    while (ref && !seen.has(ref.tag)) {
      seen.add(ref.tag);
      const item = ctx.lookupMaybe(ref, PDFDict);
      if (!item) break;
      const rawTitle = item.lookup(PDFName.of("Title"));
      const title = rawTitle instanceof PDFString || rawTitle instanceof PDFHexString ? rawTitle.decodeText() : "";
      const firstChild = item.get(PDFName.of("First"));
      nodes.push({
        title,
        pageIndex: destinationIndex(item),
        children: firstChild instanceof PDFRef ? walk(firstChild) : [],
      });
      const next = item.get(PDFName.of("Next"));
      ref = next instanceof PDFRef ? next : undefined;
    }
    return nodes;
  };

  const first = root.get(PDFName.of("First"));
  return first instanceof PDFRef ? walk(first) : [];
}

export interface MergedOutlineNode {
  title: string;
  page: PDFPage | null;
  children: MergedOutlineNode[];
}

const countAll = (nodes: MergedOutlineNode[]): number => nodes.reduce((n, x) => n + 1 + countAll(x.children), 0);

/** Writes the given tree as the document's outline. */
export function writeOutline(doc: PDFDocument, nodes: MergedOutlineNode[]): void {
  if (nodes.length === 0) return;
  const ctx = doc.context;
  const rootRef = ctx.nextRef();

  const build = (items: MergedOutlineNode[], parentRef: PDFRef): { first: PDFRef; last: PDFRef } => {
    const refs = items.map(() => ctx.nextRef());
    items.forEach((node, i) => {
      const dict = ctx.obj({}) as PDFDict;
      dict.set(PDFName.of("Title"), PDFHexString.fromText(node.title || "Untitled"));
      dict.set(PDFName.of("Parent"), parentRef);
      if (i > 0) dict.set(PDFName.of("Prev"), refs[i - 1]);
      if (i < items.length - 1) dict.set(PDFName.of("Next"), refs[i + 1]);
      if (node.page) dict.set(PDFName.of("Dest"), ctx.obj([node.page.ref, PDFName.of("Fit")]));
      if (node.children.length > 0) {
        const kids = build(node.children, refs[i]);
        dict.set(PDFName.of("First"), kids.first);
        dict.set(PDFName.of("Last"), kids.last);
        dict.set(PDFName.of("Count"), PDFNumber.of(countAll(node.children)));
      }
      ctx.assign(refs[i], dict);
    });
    return { first: refs[0], last: refs[refs.length - 1] };
  };

  const top = build(nodes, rootRef);
  const root = ctx.obj({}) as PDFDict;
  root.set(PDFName.of("Type"), PDFName.of("Outlines"));
  root.set(PDFName.of("First"), top.first);
  root.set(PDFName.of("Last"), top.last);
  root.set(PDFName.of("Count"), PDFNumber.of(countAll(nodes)));
  ctx.assign(rootRef, root);
  doc.catalog.set(PDFName.of("Outlines"), rootRef);
}

/** Maps a source outline onto the pages it was copied to. Entries whose
 * destination couldn't be resolved keep their place in the tree (so the
 * structure and any resolvable children survive) but have no target. */
export function mapOutline(nodes: OutlineNode[], copiedPages: PDFPage[]): MergedOutlineNode[] {
  return nodes.map(n => ({
    title: n.title,
    page: n.pageIndex !== null ? copiedPages[n.pageIndex] ?? null : null,
    children: mapOutline(n.children, copiedPages),
  }));
}

/** Like mapOutline, for a copy that kept only SOME pages: `pageFor` maps a source page index to its
 * copy, or has no entry for pages that were left out. Entries that point at a dropped page disappear,
 * unless a child of theirs survives (then the parent stays and points at that child's page). */
export function mapOutlineSubset(nodes: OutlineNode[], pageFor: Map<number, PDFPage>): MergedOutlineNode[] {
  const out: MergedOutlineNode[] = [];
  for (const n of nodes) {
    const children = mapOutlineSubset(n.children, pageFor);
    const own = n.pageIndex !== null ? pageFor.get(n.pageIndex) ?? null : null;
    if (!own && children.length === 0) continue;
    out.push({ title: n.title, page: own ?? children.find((c) => c.page)?.page ?? null, children });
  }
  return out;
}
